import { parseEmailAssistantContactScopeSelection } from "./EmailAssistantContactScopeModel";
import type { EmailAssistantQueryContext } from "./EmailAssistantQueryContextTypes";

import type { AdminV2LiveAlertSource } from "@/types/adminV2";

const emailAssistantLiveAlertSources: readonly AdminV2LiveAlertSource[] = [
  "support_ticket",
  "issue_report",
  "email_activity",
  "job",
  "automation_run",
  "billing_event",
  "logging_provider_event",
  "google_workspace_gmail",
  "monday_item",
  "manual_alert",
];

const isEmailAssistantLiveAlertSource = (
  value: string,
): value is AdminV2LiveAlertSource =>
  emailAssistantLiveAlertSources.includes(value as AdminV2LiveAlertSource);

const parseEmailAssistantLiveAlertTarget = (
  rawAlertId: string | null,
): EmailAssistantQueryContext["liveAlertTarget"] | undefined => {
  const alertId = rawAlertId?.trim() ?? "";
  const separatorIndex = alertId.indexOf(":");

  if (separatorIndex <= 0 || separatorIndex === alertId.length - 1) {
    return undefined;
  }

  const source = alertId.slice(0, separatorIndex);
  const sourceRecordId = alertId.slice(separatorIndex + 1).trim();

  if (!isEmailAssistantLiveAlertSource(source) || !sourceRecordId) {
    return undefined;
  }

  return {
    alertId,
    source,
    sourceRecordId,
  };
};

export const buildEmailAssistantOwnerQueryContext = (
  searchParams: URLSearchParams,
): EmailAssistantQueryContext | null => {
  const rawContactScope = searchParams.get("eaCommunicationScope");
  const contactScope =
    parseEmailAssistantContactScopeSelection(rawContactScope);
  if (rawContactScope !== null && !contactScope) return null;
  const safeSource = searchParams.get("eaSafeSource");
  const personId = searchParams.get("eaPersonId")?.trim();
  const organisationId = searchParams.get("eaOrganisationId")?.trim() ?? "";
  const isIssueReportSource = safeSource === "issue_report";
  const isContactsDashboardSource = safeSource === "crm_contacts_dashboard";
  const isCrmContactSource =
    safeSource === "crm_contact" || isContactsDashboardSource;
  const isCrmUserSource = safeSource === "crm_user_profile";
  const isLiveAlertSource = safeSource === "live_alert";
  if (contactScope && !isCrmContactSource && !isCrmUserSource) return null;
  const liveAlertTarget = isLiveAlertSource
    ? parseEmailAssistantLiveAlertTarget(searchParams.get("eaAlertId"))
    : undefined;

  if (isIssueReportSource) {
    const issueReportId = searchParams.get("eaIssueReportId")?.trim();
    if (!issueReportId) {
      return null;
    }

    const rawSourceName = searchParams.get("eaSourceName")?.trim() ?? "";
    const rawSourceType = searchParams.get("eaSourceType")?.trim() ?? "";
    const rawContextLabel = searchParams.get("eaContext")?.trim() ?? "";
    const sourceName =
      rawSourceName.length > 0
        ? rawSourceName
        : `Issue: ${issueReportId.slice(0, 8).toUpperCase()}`;
    const resolvedOrganisationId =
      organisationId.length > 0 ? organisationId : undefined;

    return {
      contextSections: [
        {
          title: "Issue record",
          subtitle: sourceName,
          emptyText: "No issue context was provided.",
          items: [
            {
              label: "Source record",
              detail: "Opened from Issue Reports.",
            },
            {
              label: "Owner boundary",
              detail:
                "Issue Reports opens this composer. Replies, notes, and screenshots stay on the issue timeline; Email & Comms owns any reviewed email send.",
            },
          ],
        },
      ],
      ...(personId
        ? {
            crmHandoff: {
              organisationId: resolvedOrganisationId,
              personId,
              sourceKind: "issue_report" as const,
              sourceName,
            },
          }
        : {}),
      issueHandoff: { issueReportId },
      recipients: [
        {
          name: "Issue reporter",
          email: "",
          sourceLabel: "Issue reporter",
        },
      ],
      sourceContext: {
        sourceName,
        sourceType: rawSourceType.length > 0 ? rawSourceType : "Issue record",
        contextLabel:
          rawContextLabel.length > 0
            ? rawContextLabel
            : "Opened from an issue's Take Action panel. Review recent communications, preferences, suppressions, sender setup, and Activity logging before any customer send.",
      },
    };
  }

  if (
    !personId ||
    (!isCrmContactSource && !isCrmUserSource && !isLiveAlertSource)
  ) {
    return null;
  }

  const sourceName = isLiveAlertSource
    ? "Live Alert handoff"
    : isCrmUserSource
      ? "CRM user profile"
      : isContactsDashboardSource
        ? "Contacts dashboard"
        : "CRM contact profile";
  const sourceType = isLiveAlertSource ? "Live Alerts" : "CRM";
  const contextLabel = isLiveAlertSource
    ? "Opened from Live Alerts. Email & Comms owns recipient preview, sender setup, preferences, Activity, and delivery safety before any email is sent."
    : isContactsDashboardSource
      ? "Opened from the Contacts dashboard. Review recent communications, preferences, suppressions, sender setup, and Activity logging before any customer send."
      : "Opened from a CRM profile. Review recent communications, preferences, suppressions, sender setup, and Activity logging before any customer send.";
  const resolvedOrganisationId =
    organisationId.length > 0 ? organisationId : undefined;

  return {
    contextSections: [
      {
        title: isLiveAlertSource ? "Live Alert handoff" : "CRM handoff",
        subtitle: isLiveAlertSource
          ? "Review-first source context"
          : isContactsDashboardSource
            ? "Safe source context from the selected contact"
            : "Safe source context from the profile action",
        emptyText: isLiveAlertSource
          ? "No Live Alert context was provided."
          : "No CRM handoff context was provided.",
        items: [
          {
            label: "Source record",
            detail: sourceName,
          },
          {
            label: "Recipient",
            detail: isLiveAlertSource
              ? "Linked CRM person. Name and email stay out of saved links until Email & Comms resolves them safely."
              : "Selected CRM person. Name and email stay out of saved links until the reviewed-send workflow resolves them safely.",
          },
          ...(isLiveAlertSource
            ? [
                {
                  label: "Owner boundary",
                  detail:
                    "Live Alerts opens this panel only. Email & Comms owns the draft, review checks, Activity logging, and any send attempt.",
                },
              ]
            : []),
          ...(organisationId
            ? [
                {
                  label: "Organisation reference",
                  detail:
                    "Received as an id only for delivery and Activity checks.",
                },
              ]
            : []),
        ],
      },
    ],
    crmHandoff: {
      ...(contactScope ? { contactScope } : {}),
      organisationId: contactScope
        ? (contactScope.organisationId ?? undefined)
        : resolvedOrganisationId,
      personId,
      sourceKind: isLiveAlertSource
        ? "live_alert"
        : isCrmUserSource
          ? "crm_user_profile"
          : "crm_contact",
      sourceName,
    },
    ...(liveAlertTarget ? { liveAlertTarget } : {}),
    recipients: [
      {
        name: "Selected CRM person",
        email: "",
        sourceLabel: sourceName,
      },
    ],
    sourceContext: {
      sourceName,
      sourceType,
      contextLabel,
      ...(isLiveAlertSource ? { alertName: "Live Alert handoff" } : {}),
    },
  };
};
